<template>
  <div class="info-card" :id="topic.id.toString()" ref="cardRef">
    <el-collapse-item :name="topic.id">
      <!-- Title -->
      <template #title>
        <span :aria-label="translation.title" class="title-container">
          <span class="title-text">{{ translation.title }}</span>
        </span>
      </template>

      <!-- Media Thumbnail -->
      <img
        v-if="topic.photo"
        :src="topic.photo"
        :alt="topic.photo_name || topic.photo"
        class="info-card__media"
      />

      <div
        class="info-card__media"
        :class="
          topic.video?.includes('tiktok.com')
            ? 'info-card__media--tiktok'
            : 'info-card__media--video'
        "
        v-if="topic.video"
        v-html="topic.video"
      ></div>

      <!-- Content -->
      <div class="info-card__content" v-loading="data.isGooglePlaceLoading">
        <div
          v-show="translation.description"
          v-html="translation.description"
          class="info-card__description"
          ref="descriptionRef"
        ></div>

        <!-- Google Place -->
        <template v-if="topic.google_place">
          <!-- Offline and hasn't loaded any data yet -->
          <div v-if="!isOnline() && !hasGooglePlaceBeenLoaded" class="info-card__offline">
            <span>
              Google data not available offline. Please connect to a network to load additional
              details.
            </span>
          </div>

          <!-- Online or loaded data -->
          <div v-else class="info-card__block">
            <!-- Address  -->
            <div
              v-if="topic.google_place.formatted_address"
              :data-color="theme.accentColor"
              class="info-card__block"
            >
              <div class="info-card__block--title">{{ helperStore.i18n('address') }}</div>
              <div class="info-card__block--content">
                <a v-if="showMapTab" @click.prevent="handleMapLocationClick" href="#">
                  {{ topic.google_place.formatted_address }}
                </a>

                <span v-else>
                  {{ topic.google_place.formatted_address }}
                </span>
              </div>
            </div>

            <!-- Phone -->
            <div
              v-if="topic.google_place.international_phone_number"
              class="info-card__block"
              :data-color="theme.accentColor"
            >
              <div class="info-card__block--title">{{ helperStore.i18n('phone') }}</div>
              <div class="info-card__block--content">
                <a :href="`tel:${topic.google_place.international_phone_number}`">
                  {{ topic.google_place.international_phone_number }}
                </a>
              </div>
            </div>

            <!-- Rating -->
            <div
              class="info-card__block"
              :data-color="theme.accentColor"
              v-if="hasGooglePlaceBeenLoaded"
            >
              <div class="info-card__block--title">
                {{ helperStore.i18n('google_reviews') }}
              </div>
              <div class="info-card__block--stars">
                <span class="rating">{{ rating }}</span>
                <el-rate disabled :model-value="rating" :colors="starColors"> </el-rate>
              </div>
            </div>

            <!-- Opening Hours -->
            <div
              class="info-card__block"
              :data-color="theme.accentColor"
              v-if="topic.google_place.opening_hours"
            >
              <el-collapse v-model="data.hoursOpened">
                <el-collapse-item name="hours" class="hours-block">
                  <template #title>
                    <div class="info-card__block--title">
                      {{ helperStore.i18n('hours') }}
                    </div>
                  </template>

                  <div
                    class="info-card__block--content"
                    v-for="weekText in topic.google_place.opening_hours.weekday_text"
                    :key="weekText"
                    :data-color="theme.accentColor"
                  >
                    {{ weekText }}
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
        </template>

        <!-- Buttons -->
        <div class="info-card__block--flex">
          <el-row>
            <!-- Go to Map -->
            <div
              v-if="showMapTab && (topic.custom_location || topic.google_place)"
              class="info-card__btn"
              @click="handleMapLocationClick"
            >
              <i class="icon-Pin-1"></i>
            </div>

            <!-- Load Google Place Info, present only if topic has GP data -->
            <div
              v-if="topic.google_place && !hasGooglePlaceBeenLoaded"
              class="info-card__btn"
              @click="loadGooglePlace"
            >
              <i class="icon-Information-2"></i>
            </div>

            <!-- Trip Advisor Link -->
            <div v-if="translation.ta_link" class="info-card__btn" @click="handleTripAdvisorClick">
              <i class="icon-Trip-Advisor"></i>
            </div>

            <!-- Website Link -->
            <div
              v-if="topic.website || topic.google_place?.website"
              class="info-card__btn"
              @click="handleWebsiteClick"
            >
              <i class="icon-Link"></i>
            </div>
          </el-row>

          <!-- Share Link -->
          <div class="info-card__btn" @click="onShareClick">
            <i class="icon-Share-3"></i>
          </div>
        </div>
      </div>
    </el-collapse-item>
  </div>
</template>

<script lang="ts" setup>
import {
  PlausibleEvents,
  PlausibleProperties,
} from '@/utils/consts'
import { sendPlausibleEvent, transformLinksWithin } from '@/utils/helpers'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ShareTypesEnum, Topic } from '@/interfaces/guide.interface'
import RouteNamesEnum from '@/router/route-names'
import { useHelperStore } from '@/pinia/helper.store'
import { useMainStore } from '@/pinia/main.store'

const helperStore = useHelperStore()
const mainStore = useMainStore()
const router = useRouter()

const descriptionRef = ref<HTMLDivElement>(null)
const cardRef = ref<HTMLDivElement>(null)
const props = defineProps<{ topic: Topic; isTopicExpanded: boolean }>()
const emit = defineEmits<{
  (e: 'show-share-overlay', payload: { type: ShareTypesEnum; topicId: number }): void
}>()
const data = reactive({ hoursOpened: '', isGooglePlaceLoading: false })
const theme = computed(() => helperStore.styleData)
const translation = computed(() => props.topic.translations[helperStore.currentLanguage])
const showMapTab = computed(() => !mainStore.propertyInfo.hide_map_tab)
const starColors = computed(() => ['#C0C4CC', theme.value.accentColor, theme.value.accentColor])
const rating = computed(() => {
  const gpRating = props.topic?.google_place?.rating

  return gpRating ? parseFloat(gpRating.toFixed(1)) : 0
})
const hasGooglePlaceBeenLoaded = computed(() => !!props.topic?.google_place?.rating)

watch(
  () => helperStore.currentLanguage,
  () => nextTick(() => updateLinkHandlers())
)

// load custom scripts when topic is expanded
const unwatchExpanded = watch(
  () => props.isTopicExpanded,
  (isTopicExpanded) => {
    if (isTopicExpanded) {
      loadCustomScripts()
      unwatchExpanded()
    }
  }
)

onMounted(() => {
  updateLinkHandlers()

  if (props.isTopicExpanded) {
    loadCustomScripts()
    unwatchExpanded()
  }
})

// Calendly & Strava require JS to be loaded and executed. They create an iframe and once we have it we can safely remove the script
async function loadCustomScripts() {
  const whitelist = [
    'https://strava-embeds.com/embed.js',
    'https://assets.calendly.com/assets/external/widget.js'
  ]
  const scripts = [...descriptionRef.value.querySelectorAll('script')]

  scripts.forEach((script) => {
    if (whitelist.includes(script.src)) {
      const scriptEl = document.createElement('script')
      scriptEl.src = script.src
      scriptEl.type = 'text/javascript'
      document.body.appendChild(scriptEl)
      scriptEl.remove()
      script.remove()
    }
  })

  loadTikTokScriptIfNeeded()
}

function loadTikTokScriptIfNeeded() {
  const TIKTOK_EMBED_URL = 'https://tiktok.com/embed.js'
  const requiresTiktokScript = cardRef.value.innerHTML.includes('tiktok.com')

  if (requiresTiktokScript) {
    const scriptEl = document.createElement('script')
    scriptEl.src = TIKTOK_EMBED_URL
    scriptEl.type = 'text/javascript'
    document.body.appendChild(scriptEl)
    scriptEl.remove()
  }
}

async function loadGooglePlace() {
  if (hasGooglePlaceBeenLoaded.value) {
    return
  }

  if (navigator.onLine && !data.isGooglePlaceLoading) {
    data.isGooglePlaceLoading = true
    await mainStore.fetchGooglePlace(props.topic)
    data.isGooglePlaceLoading = false

    sendPlausibleEvent(PlausibleEvents.TOPIC_GOOGLE_PLACE_CLICK, {
      [PlausibleProperties.TOPIC_ID]: props.topic.id
    })
  }
}

function updateLinkHandlers() {
  if (descriptionRef.value) {
    transformLinksWithin([...descriptionRef.value.getElementsByTagName('a')])
  }
}

function isOnline() {
  return navigator.onLine
}

function handleMapLocationClick() {
  helperStore.activeMapFilterType = null

  router.push({
    name: RouteNamesEnum.MAP,
    query: {
      markerId: props.topic.id.toString(),
      setBounds: 'true'
    }
  })

  sendPlausibleEvent(PlausibleEvents.TOPIC_LOCATION_CLICK, {
    [PlausibleProperties.TOPIC_ID]: props.topic.id
  })
}

function handleTripAdvisorClick() {
  sendPlausibleEvent(PlausibleEvents.TOPIC_TRIP_ADVISOR_CLICK, {
    [PlausibleProperties.TOPIC_ID]: props.topic.id
  })

  window.open(translation.value.ta_link, '_blank')
}

function handleWebsiteClick() {
  const link = props.topic.website || props.topic.google_place?.website

  sendPlausibleEvent(PlausibleEvents.TOPIC_WEBSITE_CLICK, {
    [PlausibleProperties.TOPIC_ID]: props.topic.id
  })

  window.open(link, '_blank')
}

function onShareClick() {
  emit('show-share-overlay', { type: ShareTypesEnum.TOPIC, topicId: props.topic.id })
}
</script>

<style lang="scss">
.info-card {
  span {
    position: relative;
  }

  .el-collapse-item__content {
    padding-bottom: 10px;
  }

  .title-text {
    color: inherit;
    font-family: v-bind('helperStore.headerFont');
  }

  .hours-block .el-collapse-item__arrow {
    margin-left: 8px;
    margin-top: 3px;
    font-size: 1rem;
  }

  .title-border {
    position: absolute;
    width: 100%;
    left: 0;
    height: 1px;
    top: 100%;
    background-color: v-bind('theme.accentColor');
  }

  & > .el-collapse-item > div[role='tab'] {
    padding: 14px;
  }

  &__content {
    font-size: 1rem;
    padding: $padding $padding * 2;

    a {
      overflow-wrap: break-word;
    }

    p {
      margin-top: 0;
      font-size: inherit;
      line-height: inherit;
    }

    iframe,
    video {
      width: 100%;
      min-height: 300px;
      max-height: 10000px;
    }
  }

  &__media {
    width: 100%;
    max-width: 100%;
    object-fit: contain;
    margin: 15px 0;

    &--tiktok {
      position: relative;
    }

    &--video {
      max-height: unset;
      position: relative;
      padding-bottom: 52.5%; /* 16:9 */
      padding-top: 25px;
      height: 0;

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
      }
    }
  }

  &__btn {
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    background-color: v-bind('theme.accentColor');
    cursor: pointer;

    a {
      display: flex;
    }

    svg {
      width: 25px;
      height: 25px;
      fill: currentColor;
    }

    i {
      transform: none;
      color: white;
      text-shadow: $text-shadow--icon;
    }
  }

  &__block {
    margin: 15px 0;

    &--title {
      font-size: rem(16);
      font-weight: 600;
    }

    &--flex {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &--stars {
      display: flex;
      align-items: center;

      .rating {
        margin-right: 10px;
      }

      i {
        font-size: rem(18);
        margin: 0;
        transform: none;
      }
    }

    &--content {
      position: relative;
      padding-left: 12px;

      &:before {
        content: '';
        width: 5px;
        height: 5px;
        background: currentColor;
        border-radius: 50%;
        position: absolute;
        left: 0;
        top: 10px;
      }
    }
  }

  img.inline-image {
    &--full {
      width: 100%;
    }

    &--medium {
      width: 50%;
    }

    &--small {
      width: 25%;
    }

    &--icon {
      width: 64px;
      height: 64px;
      object-fit: contain;
    }
  }

  &__offline {
    border-top: 2px solid v-bind('theme.accentColor');
    text-align: center;
    padding-top: 15px;
    margin: 15px 30px 0 30px;
    font-style: italic;
  }
}

.category-topic .el-collapse-item__header {
  padding: 15px;
  height: 75px;
  border-radius: 4px;
}

@include mediaDesktop {
  .info-card__content {
    padding: $padding $padding * 2 $padding $padding * 3;
  }

  .category-topic .el-collapse-item__header {
    padding-left: 20px;
  }
}
</style>
